import { ReactNode, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Footer from "./Footer";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Navigation links
  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About Us", path: "/about" },
    { label: "Courses", path: "/courses" },
    { label: "Teams", path: "/teams" },
    { label: "Merchandise", path: "/merchandise" },
    { label: "Community", path: "/community" },
    { label: "Events", path: "/events" },
    { label: "Dance Lab", path: "/dance-lab" },
    { label: "Pricing", path: "/pricing" },
    { label: "Support", path: "/support" },
  ];

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check if link is active
  const isActiveLink = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    if (path.startsWith("/#")) {
      return location.pathname === "/" && location.hash === path.substring(1);
    }
    return location.pathname === path;
  };

  // Handle link clicks
  const handleLinkClick = (path: string) => {
    if (path.startsWith("/#")) {
      // Handle anchor links
      const element = document.querySelector(path.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#0A0A0A" }}>
      {/* Sticky NavBar */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 w-full"
      >
        <div
          className="relative px-6 lg:px-12 py-4 transition-all duration-300"
          style={{
            backgroundColor: isScrolled ? "rgba(10, 10, 10, 0.95)" : "rgba(10, 10, 10, 0.7)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderBottom: `1px solid ${isScrolled ? "rgba(230, 126, 34, 0.2)" : "rgba(255, 255, 255, 0.05)"}`,
          }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-8">
            {/* Logo/Brand */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to="/"
                onClick={(e) => {
                  // Always scroll to top of homepage when logo is clicked
                  if (location.pathname === "/") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    // Navigate to homepage and scroll to top after navigation
                    setTimeout(() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }, 0);
                  }
                }}
                className="flex items-center transition-opacity duration-300 hover:opacity-80"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="h-9 md:h-[42px] max-w-[150px] md:max-w-[188px]"
                >
                  {/* NOTE: Logo should be optimized to ~256x160px (2x retina: 512x320px) for nav display */}
                  <img
                    src="/assets/whitelogo.png"
                    alt="Klau y Ros"
                    className="h-full w-auto object-contain"
                    width={256}
                    height={160}
                    loading="eager"
                    style={{ 
                      pointerEvents: "none",
                      backgroundColor: "transparent"
                    }}
                  />
                </motion.div>
              </Link>
            </motion.div>

            {/* Desktop & Tablet Navigation - Integrated Design */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="hidden md:flex items-center gap-1 flex-1 justify-center"
            >
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleLinkClick(link.path)}
                  className="relative px-3 md:px-4 py-2 text-sm lg:text-base font-medium transition-all duration-300 whitespace-nowrap group"
                  style={{
                    color: isActiveLink(link.path) ? "#FFFFFF" : "rgba(255, 255, 255, 0.7)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActiveLink(link.path)) {
                      e.currentTarget.style.color = "#FFFFFF";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActiveLink(link.path)) {
                      e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)";
                    }
                  }}
                >
                  {link.label}
                  {/* Active gradient underline */}
                  {isActiveLink(link.path) && (
                    <motion.div
                      layoutId="activeUnderline"
                      className="absolute bottom-0 left-0 right-0 h-0.5"
                      style={{
                        background: "linear-gradient(90deg, transparent, #E67E22, transparent)",
                        boxShadow: "0 0 8px rgba(230, 126, 34, 0.6)",
                      }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  {/* Hover gradient underline */}
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                    }}
                  />
                </Link>
              ))}
            </motion.div>

            {/* CTA Button - Desktop & Tablet */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="hidden md:block"
            >
              <a
                href="https://klauyros.artoverse.club/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.button
                  className="theme-button-primary"
                  whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(230, 126, 34, 0.5)" }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: "10px 24px",
                    fontSize: "16px",
                    fontWeight: 600,
                    borderRadius: "8px",
                    boxShadow: "0 0 20px rgba(255, 122, 0, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  Enter Studio →
                </motion.button>
              </a>
            </motion.div>

            {/* Mobile Hamburger */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-full transition-colors duration-300"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                color: isMobileMenuOpen ? "#E67E22" : "#FFFFFF",
              }}
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={24} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                backdropFilter: "blur(4px)",
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed top-[80px] left-0 right-0 z-50 w-full md:hidden"
              style={{
                backgroundColor: "rgba(10, 10, 10, 0.98)",
                backdropFilter: "blur(20px)",
                borderBottom: "1px solid rgba(230, 126, 34, 0.2)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
              }}
            >
              <div className="p-6 space-y-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    whileHover={{ x: 8 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => handleLinkClick(link.path)}
                      className="relative block px-4 py-3 text-lg font-medium transition-all duration-300"
                      style={{
                        color: isActiveLink(link.path) ? "#FFFFFF" : "rgba(255, 255, 255, 0.8)",
                      }}
                    >
                      {link.label}
                      {/* Active gradient underline for mobile */}
                      {isActiveLink(link.path) && (
                        <div 
                          className="absolute bottom-1 left-4 right-4 h-0.5"
                          style={{
                            background: "linear-gradient(90deg, transparent, #E67E22, transparent)",
                            boxShadow: "0 0 8px rgba(230, 126, 34, 0.6)",
                          }}
                        />
                      )}
                    </Link>
                  </motion.div>
                ))}

                {/* Mobile CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="pt-4 border-t"
                  style={{ borderColor: "rgba(255, 102, 0, 0.2)" }}
                >
                  <a
                    href="https://klauyros.artoverse.club/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <button
                      className="theme-button-primary w-full"
                      style={{
                        padding: "12px 24px",
                        fontSize: "18px",
                        fontWeight: 600,
                        borderRadius: "8px",
                        boxShadow: "0 0 20px rgba(230, 126, 34, 0.3)",
                      }}
                    >
                      Enter Studio →
                    </button>
                  </a>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;


